import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "../lib/api";
import Navbar from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "../components/ui/dialog";
import { X, Heart, Sparkles, MapPin, Crown, Rocket, SlidersHorizontal, Zap } from "lucide-react";
import { toast } from "sonner";

const HSV_ALL = ["HSV-1", "HSV-2", "Both", "Private"];
const LOOKING_ALL = ["Relationship", "Dating", "Friendship", "Casual", "Community/Support"];

function hsvClass(v) {
  if (!v) return "hsv-private";
  const s = v.toLowerCase();
  if (s.includes("both")) return "hsv-both";
  if (s.includes("hsv-1") || s === "hsv-1") return "hsv-1";
  if (s.includes("hsv-2") || s === "hsv-2") return "hsv-2";
  return "hsv-private";
}

export default function Discover() {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exiting, setExiting] = useState(null); // "left" | "right"
  const [premium, setPremium] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filters, setFilters] = useState({ min_age: "", max_age: "", hsv_status: [], looking_for: [] });
  const navigate = useNavigate();

  const loadPremium = async () => {
    try { const { data } = await api.get("/premium/status"); setPremium(data); } catch {}
  };

  const load = async (activeFilters = null) => {
    setLoading(true);
    try {
      const useFilters = activeFilters || filters;
      const hasFilters = useFilters.min_age || useFilters.max_age || useFilters.hsv_status.length || useFilters.looking_for.length;
      const req = hasFilters
        ? api.post("/discover", {
            min_age: useFilters.min_age ? parseInt(useFilters.min_age) : null,
            max_age: useFilters.max_age ? parseInt(useFilters.max_age) : null,
            hsv_status: useFilters.hsv_status.length ? useFilters.hsv_status : null,
            looking_for: useFilters.looking_for.length ? useFilters.looking_for : null,
          })
        : api.get("/discover");
      const { data } = await req;
      setCandidates(data);
    } catch {
      toast.error("Couldn't load matches. Try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); loadPremium(); }, []);

  const current = candidates[0];

  const applyFilters = () => {
    if (!premium?.is_premium) {
      toast.error("Advanced filters are a Premium feature");
      setFiltersOpen(false);
      navigate("/premium");
      return;
    }
    setFiltersOpen(false);
    load(filters);
  };

  const clearFilters = () => {
    const empty = { min_age: "", max_age: "", hsv_status: [], looking_for: [] };
    setFilters(empty);
    load(empty);
  };

  const toggleChip = (key, val) => {
    setFilters((f) => ({
      ...f,
      [key]: f[key].includes(val) ? f[key].filter((x) => x !== val) : [...f[key], val],
    }));
  };

  const boost = async () => {
    if (!premium?.is_premium) {
      navigate("/premium");
      return;
    }
    try {
      const { data } = await api.post("/premium/boost");
      toast.success("Boosted! You'll appear on top of everyone's Discover for 30 minutes.");
      await loadPremium();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't boost right now");
    }
  };

  const swipe = async (action) => {
    if (!current) return;
    setExiting(action === "like" ? "right" : "left");
    try {
      const { data } = await api.post("/swipe", {
        target_user_id: current.user_id, action
      });
      if (data.match) {
        const cleanName = (current.name || "").replace(/^\[TEST\]\s*/, "");
        toast.success(`It's a match with ${cleanName.split(" ")[0] || "them"}!`);
      }
    } catch (err) {
      if (err.response?.status === 402) {
        toast.error(err.response?.data?.detail || "Upgrade to Premium for unlimited likes");
        setExiting(null);
        navigate("/premium");
        return;
      }
      toast.error("Couldn't record swipe. Please try again.");
      setExiting(null);
      return;
    }
    // Refresh premium status to update remaining likes counter
    if (action === "like") loadPremium();
    setTimeout(() => {
      setCandidates((c) => c.slice(1));
      setExiting(null);
    }, 250);
  };

  return (
    <div className="min-h-screen bg-[#FFFDF9]">
      <Navbar />
      <main className="max-w-2xl mx-auto px-4 py-8">
        <div className="text-center mb-4">
          <h1 className="font-serif text-3xl sm:text-4xl font-bold text-[#2C2623]">Discover</h1>
          <p className="text-[#6E655F] mt-2">Kind, honest people ready to meet you.</p>
        </div>

        {/* Premium toolbar */}
        <div className="flex flex-wrap items-center justify-center gap-2 mb-6" data-testid="discover-toolbar">
          <Dialog open={filtersOpen} onOpenChange={setFiltersOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="discover-filters-btn"
                className="rounded-full border-[#E8DEC8] gap-2">
                <SlidersHorizontal className="w-4 h-4" />
                Filters
                {!premium?.is_premium && <Crown className="w-3.5 h-3.5 text-[#B45309]" />}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl flex items-center gap-2">
                  Advanced filters <Crown className="w-4 h-4 text-[#B45309]" />
                </DialogTitle>
              </DialogHeader>
              {!premium?.is_premium ? (
                <div className="text-center py-6" data-testid="filters-premium-gate">
                  <p className="text-[#6E655F] mb-4">Filter Discover by age, HSV status, and what people are looking for.</p>
                  <Button onClick={() => { setFiltersOpen(false); navigate("/premium"); }}
                    data-testid="filters-upgrade-btn"
                    className="rounded-full bg-[#D95D39] hover:bg-[#C24C29]">Try Premium</Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label>Min age</Label>
                      <Input type="number" min={18} max={99} value={filters.min_age}
                        onChange={(e) => setFilters({ ...filters, min_age: e.target.value })}
                        data-testid="filter-min-age" />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Max age</Label>
                      <Input type="number" min={18} max={99} value={filters.max_age}
                        onChange={(e) => setFilters({ ...filters, max_age: e.target.value })}
                        data-testid="filter-max-age" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>HSV status</Label>
                    <div className="flex flex-wrap gap-2">
                      {HSV_ALL.map((h) => (
                        <button key={h} type="button" onClick={() => toggleChip("hsv_status", h)}
                          data-testid={`filter-hsv-${h}`}
                          className={`px-3 py-1 rounded-full text-sm border ${
                            filters.hsv_status.includes(h)
                              ? "bg-[#FFF0EC] border-[#D95D39] text-[#C24C29]"
                              : "bg-white border-[#E8DEC8] text-[#2C2623]"
                          }`}>{h}</button>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Looking for</Label>
                    <div className="flex flex-wrap gap-2">
                      {LOOKING_ALL.map((l) => (
                        <button key={l} type="button" onClick={() => toggleChip("looking_for", l)}
                          data-testid={`filter-looking-${l.replace(/\s|\//g, "-")}`}
                          className={`px-3 py-1 rounded-full text-sm border ${
                            filters.looking_for.includes(l)
                              ? "bg-[#FFF0EC] border-[#D95D39] text-[#C24C29]"
                              : "bg-white border-[#E8DEC8] text-[#2C2623]"
                          }`}>{l}</button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              {premium?.is_premium && (
                <DialogFooter className="mt-4">
                  <Button variant="outline" onClick={clearFilters} data-testid="filters-clear-btn">Clear</Button>
                  <Button onClick={applyFilters} data-testid="filters-apply-btn"
                    className="rounded-full bg-[#D95D39] hover:bg-[#C24C29]">Apply</Button>
                </DialogFooter>
              )}
            </DialogContent>
          </Dialog>

          <Button variant="outline" onClick={boost} data-testid="discover-boost-btn"
            className="rounded-full border-[#E8DEC8] gap-2">
            <Rocket className="w-4 h-4" />
            {premium?.boost_active_until ? "Boost active" : "Boost me"}
            {!premium?.is_premium && <Crown className="w-3.5 h-3.5 text-[#B45309]" />}
          </Button>

          {premium && !premium.is_premium && (
            <Badge onClick={() => navigate("/premium")}
              data-testid="discover-likes-remaining"
              className="cursor-pointer bg-[#FEF3D6] text-[#B45309] hover:bg-[#F5D77E] gap-1">
              <Zap className="w-3 h-3" /> {premium.likes_remaining_today} {premium.likes_remaining_today === 1 ? "like" : "likes"} left today
            </Badge>
          )}
        </div>

        <div className="relative min-h-[560px] flex justify-center items-start" data-testid="swipe-card-container">
          {loading ? (
            <div className="text-[#6E655F] pt-24">Loading kind faces…</div>
          ) : !current ? (
            <Card className="p-10 text-center rounded-3xl border-[#E8DEC8] bg-white max-w-md" data-testid="discover-empty-card">
              <Sparkles className="w-10 h-10 text-[#E09F3E] mx-auto mb-4" />
              <h3 className="font-serif text-2xl font-semibold mb-2">You've seen everyone!</h3>
              <p className="text-[#6E655F] mb-4">
                Check back soon. New members join every day.
                <br />
                <span className="text-sm">
                  Or fine-tune your <Link to="/profile" className="text-[#D95D39] underline" data-testid="discover-empty-prefs-link">gender &amp; preferences</Link> to open the pool.
                </span>
              </p>
              <Button onClick={load} data-testid="discover-refresh-btn" className="rounded-full bg-[#D95D39] hover:bg-[#C24C29]">
                Refresh
              </Button>
            </Card>
          ) : (
            <AnimatePresence>
              <motion.div
                key={current.user_id}
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{
                  x: exiting === "right" ? 400 : exiting === "left" ? -400 : 0,
                  rotate: exiting === "right" ? 20 : exiting === "left" ? -20 : 0,
                  opacity: 0,
                }}
                transition={{ duration: 0.25 }}
                className="w-full max-w-md"
              >
                <Card className="overflow-hidden rounded-3xl border-[#E8DEC8] bg-white shadow-xl shadow-[#D95D39]/5">
                  <div className="relative aspect-[4/5] bg-[#F9F5EE]">
                    {current.picture ? (
                      <img src={current.picture} alt={current.name}
                        className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-6xl font-serif text-[#D95D39]">
                        {current.name?.[0]}
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/0 to-transparent" />
                    {current.is_test && (
                      <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-[#FEF3D6] text-[#B45309] text-xs font-bold uppercase tracking-wider border border-[#F5D77E]"
                        data-testid="discover-test-badge">
                        Test data
                      </div>
                    )}
                    {current.is_boosted && (
                      <div className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-[#D95D39] text-white text-xs font-bold uppercase tracking-wider flex items-center gap-1"
                        data-testid="discover-boost-badge">
                        <Rocket className="w-3 h-3" /> Boosted
                      </div>
                    )}
                    <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span className={`text-xs px-3 py-1 rounded-full font-semibold ${hsvClass(current.hsv_status)}`}>
                          {current.hsv_status || "Private"}
                        </span>
                        {current.is_identity_verified && (
                          <span data-testid="discover-verified-badge"
                            className="text-[10px] px-2 py-0.5 rounded-full bg-white/90 text-[#2A524A] font-bold uppercase tracking-wider border border-[#B7D4C7]">
                            ✓ Verified
                          </span>
                        )}
                      </div>
                      <h2 className="font-serif text-3xl font-bold">
                        {current.name}{current.age ? <span className="font-sans font-normal">, {current.age}</span> : null}
                      </h2>
                      {current.location && (
                        <div className="flex items-center gap-1 text-white/90 text-sm mt-1">
                          <MapPin className="w-3.5 h-3.5" /> {current.location}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="p-6 space-y-4">
                    {current.bio && <p className="text-[#2C2623] leading-relaxed">{current.bio}</p>}
                    {current.interests?.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {current.interests.map((i) => (
                          <Badge key={i} variant="secondary"
                            className="bg-[#F9F5EE] text-[#2C2623] border-[#E8DEC8] rounded-full">{i}</Badge>
                        ))}
                      </div>
                    )}
                  </div>
                </Card>

                <div className="flex justify-center items-center gap-6 mt-8">
                  <Button onClick={() => swipe("pass")} data-testid="swipe-pass-button"
                    className="w-16 h-16 rounded-full bg-white border-2 border-[#E8DEC8] hover:bg-[#F9F5EE] shadow-md">
                    <X className="w-7 h-7 text-[#6E655F]" strokeWidth={2} />
                  </Button>
                  <Button onClick={() => swipe("like")} data-testid="swipe-like-button"
                    className="w-20 h-20 rounded-full bg-gradient-to-br from-[#D95D39] to-[#E88796] hover:brightness-105 shadow-xl shadow-[#D95D39]/25">
                    <Heart className="w-9 h-9 text-white fill-white" />
                  </Button>
                </div>
              </motion.div>
            </AnimatePresence>
          )}
        </div>
      </main>
    </div>
  );
}
